import { Icon } from '@/components/Icon';
import { InfoRow } from '@/components/InfoRow';
import { STATUS_TONE, STATUS_LABEL, TARIFF_LABEL, TYPE_LABEL, formatDate, formatBalance } from '@/lib/clientFormat';
import type { ClientEditOptions } from '@/api/clients';
import type { WizardState } from './wizardState';

/**
 * Шаг 6 — та же раскладка, что режим просмотра "Общее" в ClientDetailPage.tsx
 * (пары InfoRow слева/справа), плюс "Подключено тестов" — итог шага 4. Только
 * просмотр — единственное действие отсюда, кнопка "Создать", в футере
 * ClientCreatePage.tsx.
 */
export function StepReview({
  state,
  editOptions,
}: {
  state: WizardState;
  editOptions: ClientEditOptions | null;
}) {
  const tutorNames = (editOptions?.tutors ?? [])
    .filter((t) => state.tutorIds.includes(t.id))
    .map((t) => t.name)
    .join(', ');
  const templateName = state.operatorTemplateId
    ? (editOptions?.operatorTemplates.find((t) => String(t.id) === state.operatorTemplateId)?.name ?? '–')
    : 'По умолчанию';
  const contactName = [state.lastName, state.firstName, state.middleName].filter(Boolean).join(' ');
  const issuedAmount = Number(state.balanceAction.amount);
  const tariffLabel = TARIFF_LABEL[state.tariffType] ?? state.tariffType;
  const tariffValue =
    state.tariffType === 'ue' && Number.isFinite(issuedAmount) && issuedAmount > 0
      ? `${tariffLabel} (выдано ${formatBalance(issuedAmount)})`
      : tariffLabel;

  return (
    <div className="detail__grid-2">
      <div className="card">
        <h2 className="detail__subtitle">Информация о компании</h2>
        <dl className="info-list">
          <InfoRow label="Наименование компании" value={state.name} />
          <div className="info-list__row">
            <dt>Статус аккаунта</dt>
            <dd>
              <span className={`badge badge--${STATUS_TONE[state.status] ?? 'neutral'}`}>
                <span className="badge__dot" />
                {STATUS_LABEL[state.status] ?? state.status}
              </span>
            </dd>
          </div>
          <div className="info-list__row">
            <dt>Кабинет в Maintest 5i</dt>
            <dd className="info-list__value-row">
              <span>{state.mt1Login || '–'}</span>
              <span className={`badge badge--${state.isMt1HallAllowed ? 'good' : 'neutral'}`}>
                {state.isMt1HallAllowed ? 'Вход разрешён' : 'Вход не разрешён'}
              </span>
            </dd>
          </div>
          <InfoRow label="Тип" value={TYPE_LABEL[state.type] ?? state.type} />
          <InfoRow label="Тип тарификации" value={tariffValue} />
          <InfoRow label="Кураторы" value={tutorNames || null} />
          <InfoRow label="Дата отключения" value={state.validity ? formatDate(state.validity) : 'Бессрочно'} />
          <InfoRow label="Подключено тестов" value={String(state.selectedTests.size)} />
        </dl>

        {state.notes && (
          <>
            <div className="detail__divider" />
            <h2 className="detail__subtitle detail__subtitle--sm">Заметки</h2>
            <p className="detail__notes">{state.notes}</p>
          </>
        )}
      </div>

      <div className="stack">
        <div className="card">
          <h2 className="detail__subtitle">Основной контакт</h2>
          <div className="contact-row">
            <Icon name="user" size={16} />
            <span>
              {contactName || '–'}
              {state.position && <span className="cell-muted"> · {state.position}</span>}
            </span>
          </div>
          {state.email && (
            <div className="contact-row">
              <Icon name="mail" size={16} />
              <span>{state.email}</span>
            </div>
          )}
          {state.phone && (
            <div className="contact-row">
              <Icon name="phone" size={16} />
              <span>{state.phone}</span>
            </div>
          )}
        </div>

        <div className="card">
          <h2 className="detail__subtitle">Персональные данные</h2>
          <dl className="info-list">
            <InfoRow label="Шаблон соглашения" value={templateName} />
            <InfoRow label="ОГРН" value={state.ogrn} />
            <InfoRow label="Юридический адрес" value={state.address} />
          </dl>
        </div>
      </div>
    </div>
  );
}
